/**
 * Subjects entity config — drives EntityTable and EntityDetailShell (11E.4).
 *
 * Max Students is intentionally NOT surfaced here (kept in the backend
 * schema for migrated data / possible future validation, but hidden in the
 * UI per the 11E.4 spec).
 */

import React from 'react';
import { Badge, StatusBadge } from '@/components/common/Badge';
import type { EntityTableConfig, EntityDetailConfig } from '@/components/entity/types';
import type { SubjectResponse } from '@features/catalog/api/subjects';
import { formatPaise } from '@features/catalog/config/ui';
import { SubjectNameCell } from './subjectsEntityComponents';

export const subjectsTableConfig: EntityTableConfig<SubjectResponse> = {
  entityName: 'subject',
  apiPath: '/v1/catalog/subjects',
  routeBase: '/subjects',
  testIdPrefix: 'candeza-subject',
  defaultSort: 'name',
  defaultOrder: 'asc',
  defaultFilters: { status: 'active' },

  columns: [
    {
      key: 'name',
      header: 'Subject',
      isPrimary: true,
      sortable: true,
      helpKey: 'subjects.name',
      render: (s) => <SubjectNameCell subject={s} />,
    },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      helpKey: 'subjects.category',
      render: (s) => <Badge variant="default" size="sm">{s.category}</Badge>,
    },
    {
      key: 'duration_minutes',
      header: 'Duration',
      hideOnMobile: true,
      helpKey: 'subjects.duration',
      render: (s) => `${s.duration_minutes} min`,
    },
    {
      key: 'price_per_session',
      header: 'Price / session',
      hideOnMobile: true,
      helpKey: 'subjects.price',
      render: (s) => formatPaise(s.price_per_session),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (s) => <StatusBadge status={s.status} />,
    },
  ],

  filters: [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      helpKey: 'subjects.filter.status',
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
    },
  ],

  rowActions: ['view', 'edit', 'archive'],
};

export function buildSubjectDetailConfig(props: {
  renderOverviewTab: (subjectId: string) => React.ReactNode;
  renderPackagesTab?: (subjectId: string) => React.ReactNode;
}): EntityDetailConfig<SubjectResponse> {
  return {
    entityName: 'subject',
    routeBase: '/subjects',
    testIdPrefix: 'candeza-subject',
    getTitle: (s) => s.name,
    getSubtitle: (s) => s.category,
    getStatus: (s) => s.status,
    tabs: [
      { id: 'overview', label: 'Overview', lazyLoad: false, render: props.renderOverviewTab },
      {
        id: 'packages',
        label: 'Packages using this subject',
        render: props.renderPackagesTab ?? (() => <p style={{ color: '#64748b' }}>Not wired.</p>),
      },
    ],
  };
}

